import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../hooks/useAuth.js'
import ConnectionBadge from './ConnectionBadge.jsx'

const topNavItems = [
  { label: 'Dashboard', id: 'home' },
  { label: 'Zeitleiste', id: 'report' },
  { label: 'Notifications', id: 'notifications' },
]

const profileMenuItems = [
  { icon: 'settings', label: 'Settings' },
]

const popoverStyle = {
  background: 'rgba(27,28,27,0.95)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid rgba(255,255,255,0.07)',
  boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
  transformOrigin: 'top right',
}

export default function Header({ activeNav, setActiveNav, fullscreen, isTabletMode, onTabletToggle, onOpenMenu, onOpenTimeline, unreadCount = 0 }) {
  const { user } = useAuth()
  const firstName = user?.displayName || user?.name?.split(' ')[0] || ''   // the name from Settings › Users
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrollHidden, setScrollHidden] = useState(false)
  const [navSeen, setNavSeen] = useState(activeNav)
  const menuRef = useRef(null)

  // A page change always brings the header back (the new page may not scroll at all).
  if (navSeen !== activeNav) { setNavSeen(activeNav); setScrollHidden(false) }

  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  // Phones: the header slides away while scrolling down and returns on the way up.
  // It is always shown near the top (iOS reports negative scrollY while bouncing
  // there), when the page is too short to scroll, and during and just after a pull
  // at the top: the browser's own pull-to-refresh (Chrome) shifts the page as its
  // bar comes and goes, which read as scrolling down and hid the header at the top.
  // Hiding also needs real scrolling past the top zone (two readings beyond it and a
  // little travel), so no single jump — toolbar, inset — can flip it.
  useEffect(() => {
    let last = window.scrollY, travel = 0
    let pullFrom = null, pulling = false, calmUntil = 0
    const reveal = y => { travel = 0; last = y; setScrollHidden(false) }
    const update = () => {
      const y = window.scrollY
      const room = document.documentElement.scrollHeight - window.innerHeight
      if (window.innerWidth >= 768 || y <= 60 || room <= 60 || pulling || performance.now() < calmUntil) return reveal(y)
      if (last <= 60) { last = y; return }          // just left the top zone
      const d = y - last
      last = y
      if (d === 0) return
      if (Math.sign(d) !== Math.sign(travel)) travel = 0
      travel += d
      if (travel > 12) setScrollHidden(true)
      else if (travel < -8) setScrollHidden(false)
    }
    // A finger that starts at the top and moves down is a pull, not a scroll.
    const onStart = e => { pullFrom = window.scrollY <= 0 ? e.touches[0].clientY : null; pulling = false }
    const onMove = e => {
      if (pullFrom != null && !pulling && e.touches[0].clientY - pullFrom > 8) { pulling = true; reveal(window.scrollY) }
    }
    const onEnd = () => {
      if (pulling) calmUntil = performance.now() + 900   // the bounce / bar settling after it
      pullFrom = null; pulling = false
      update()
    }
    const ro = new ResizeObserver(update)          // content grew or shrank
    ro.observe(document.body)
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd, { passive: true })
    window.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      ro.disconnect()
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  function handleLogout() {
    window.location.href = '/auth/logout'
  }

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: fullscreen ? 0 : 1, y: fullscreen || scrollHidden ? '-100%' : 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 md:px-10 py-4"
      style={{
        background: 'rgba(19, 19, 19, 0.75)',
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        pointerEvents: fullscreen ? 'none' : undefined,
      }}
    >
      {/* Mobile burger + Logo */}
      <div className="flex items-center gap-3">
        {/* Burger — mobile only */}
        <button
          onClick={onOpenMenu}
          className="md:hidden flex flex-col items-center justify-center gap-[5px]"
          style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', flexShrink: 0 }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                display: 'block',
                width: i === 1 ? 14 : 20,
                height: 2,
                borderRadius: 2,
                background: '#b0ceae',
                alignSelf: i === 1 ? 'flex-end' : 'center',
              }}
            />
          ))}
        </button>

      {/* Logo */}
      <button
        onClick={() => setActiveNav('my-dashboard')}
        className="flex items-center gap-3 transition-opacity duration-200 hover:opacity-80"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #b0ceae, #8ba889)' }}
        >
          <span
            className="material-symbols-outlined text-on-primary"
            style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1, 'wght' 500" }}
          >
            home
          </span>
        </motion.div>
        <span
          className="font-headline font-bold text-xl tracking-tight"
          style={{ color: '#b0ceae', letterSpacing: '-0.03em' }}
        >
          YOKAN
        </span>
      </button>
      <ConnectionBadge />
      </div>

      {/* Center Nav */}
      <nav className="hidden md:flex items-center gap-1">
        {topNavItems.map((item) => {
          const isActive = activeNav === item.id
          return (
            <button
              key={item.id}
              onClick={() => setActiveNav(item.id)}
              className="px-4 py-2 rounded-full text-sm transition-all duration-200"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: isActive ? 700 : 500,
                color: isActive ? '#b0ceae' : 'rgba(195, 200, 191, 0.6)',
                background: isActive ? 'rgba(176,206,174,0.1)' : 'transparent',
              }}
            >
              {item.label}
            </button>
          )
        })}
      </nav>

      {/* Right Actions */}
      <div className="flex items-center gap-2">
        {user?.role === 'viewer' && (
          <div
            title="Nur-Lese-Zugriff"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(255,181,155,0.08)', border: '1px solid rgba(255,181,155,0.2)' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '0.9rem', color: '#ffb59b' }}>edit_off</span>
            <span className="font-label text-[10px] hidden sm:block" style={{ color: '#ffb59b', letterSpacing: '0.04em' }}>Lesezugriff</span>
          </div>
        )}

        {/* Tablet mode toggle */}
        <button
          onClick={onTabletToggle}
          title="Tablet carousel mode"
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200"
          style={{
            color: isTabletMode ? '#b0ceae' : 'rgba(195,200,191,0.6)',
            background: isTabletMode ? 'rgba(176,206,174,0.1)' : 'transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(176,206,174,0.1)'; e.currentTarget.style.color = '#b0ceae' }}
          onMouseLeave={e => { e.currentTarget.style.background = isTabletMode ? 'rgba(176,206,174,0.1)' : 'transparent'; e.currentTarget.style.color = isTabletMode ? '#b0ceae' : 'rgba(195,200,191,0.6)' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>tablet</span>
        </button>

        {/* Notifications icon */}
        <button
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200"
          style={{ color: 'rgba(195,200,191,0.6)', position: 'relative' }}
          onClick={onOpenTimeline}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(176,206,174,0.1)'; e.currentTarget.style.color = '#b0ceae' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(195,200,191,0.6)' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>notifications</span>
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.span
                key="badge"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 14,
                  height: 14,
                  borderRadius: 99,
                  background: '#ff6450',
                  color: '#fff',
                  fontSize: unreadCount > 9 ? '7px' : '8px',
                  fontWeight: 700,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  lineHeight: 1,
                  boxShadow: '0 0 0 1.5px rgba(19,19,19,0.9)',
                  pointerEvents: 'none',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </motion.span>
            )}
          </AnimatePresence>
        </button>

{/* Avatar + dropdown */}
        <div className="relative ml-1" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-2.5 rounded-full transition-all duration-200 pl-2 pr-1 py-1"
            style={{
              background: menuOpen ? 'rgba(176,206,174,0.1)' : 'transparent',
            }}
          >
            {firstName && (
              <span
                className="hidden sm:block text-sm"
                style={{ color: 'rgba(195,200,191,0.7)', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}
              >
                {firstName}
              </span>
            )}
            <div
              className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
              style={{ boxShadow: '0 0 0 2px rgba(176,206,174,0.25)' }}
            >
              <img src={user?.picture || undefined} alt={firstName} className="w-full h-full object-cover" />
            </div>
          </button>

          {/* Dropdown card */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                className="absolute right-0 mt-2 w-64 rounded-2xl overflow-hidden"
                style={popoverStyle}
              >
                {/* User info */}
                <div className="flex items-center gap-3 px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" style={{ boxShadow: '0 0 0 2px rgba(176,206,174,0.25)' }}>
                    <img src={user?.picture || undefined} alt={firstName} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {user?.name ?? ''}
                    </p>
                    <p className="text-xs truncate text-outline">
                      {user?.email ?? ''}
                    </p>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-1.5">
                  {profileMenuItems.map(item => (
                    <button
                      key={item.icon}
                      onClick={() => { setMenuOpen(false); setActiveNav('dashboard-settings') }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150"
                      style={{ color: '#c3c8bf', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', color: '#8d928a' }}>{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                </div>

                {/* Logout */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} className="py-1.5">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150"
                    style={{ color: '#ffb59b', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,181,155,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', color: '#ffb59b' }}>logout</span>
                    Sign out
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.header>
  )
}
